import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Coins, TrendingDown, ShieldCheck, Infinity as InfinityIcon } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { portalMeApi } from '../../services/api';

/**
 * Member-facing allocation panel for the Portal Dashboard. Renders
 * the user's personal balance, lifetime allocated by the org,
 * lifetime consumed, and a 14-day burn sparkline. Super-admins
 * (and personal tenants) see an "Unlimited" variant instead of
 * the bar/percentage.
 */
const fetchMine = (days = 14) => portalMeApi.allocation(days).then((r) => r.data);

export const MyAllocationCard = () => {
  const { data } = useQuery({
    queryKey: ['myAllocation', 14],
    queryFn: () => fetchMine(14),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  if (!data) return null;

  // Only super-admin gets the "Unlimited" badge — all other users
  // (including personal tenants and non-strict-allocation orgs) MUST
  // see their actual balance so they can manage usage. For non-strict
  // accounts, the effective spend pool includes both their personal
  // allocation AND the shared org pool.
  const isUnlimited = !!data.is_super_admin;
  const strict = !!data.strict_allocation;
  const personalBalance = Number(data.personal_balance || 0);
  const poolBalance = Number(data.pool_balance || 0);
  // For non-strict accounts, the user can spend from both wallets —
  // show the combined balance so they don't think they're stuck on
  // an empty personal allocation when the pool has credits.
  const balance = strict ? personalBalance : (personalBalance + poolBalance);
  const allocated = Number(data.lifetime_allocated || 0);
  const consumed = Number(data.lifetime_consumed || 0);
  const pct = allocated > 0 ? Math.min(100, Math.round((consumed / allocated) * 100)) : 0;
  const runningLow = !isUnlimited && balance > 0 && balance < 200;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5 col-span-1"
      style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
      data-testid="my-allocation-card"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>My allocation</div>
          <h3 className="font-heading text-sm font-semibold text-nexus-text-primary mt-0.5">
            {isUnlimited ? 'Unlimited credits' : (strict ? 'Personal credits' : 'Shared wallet')}
          </h3>
        </div>
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}
        >
          {isUnlimited ? <ShieldCheck size={16} /> : <Coins size={16} />}
        </div>
      </div>

      {isUnlimited ? (
        <div className="flex items-center gap-3 mb-3">
          <InfinityIcon size={38} className="text-nexus-accent-mint" />
          <div>
            <div className="font-heading text-2xl font-bold text-nexus-text-primary">Unlimited</div>
            <div className="text-[11px] text-nexus-text-tertiary">
              {data.is_super_admin ? 'Super-admin · bypasses metering' : 'Personal tenant · shared wallet'}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="font-heading text-3xl font-bold text-nexus-text-primary tabular-nums">
            {balance.toLocaleString(undefined, { maximumFractionDigits: 1 })}
            <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary ml-1.5">credits</span>
          </div>
          <div className="text-[11px] text-nexus-text-tertiary mt-0.5">
            {strict
              ? (allocated > 0
                  ? `${consumed.toLocaleString()} / ${allocated.toLocaleString()} consumed lifetime (${pct}%)`
                  : 'No allocation yet — ask your org admin to top up')
              : (poolBalance > 0
                  ? `${personalBalance.toLocaleString(undefined, { maximumFractionDigits: 1 })} personal + ${poolBalance.toLocaleString(undefined, { maximumFractionDigits: 1 })} shared pool`
                  : `${consumed.toLocaleString()} consumed lifetime`)}
          </div>
          {/* Burn bar */}
          {allocated > 0 && (
            <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--layer-2)' }}>
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${pct}%`,
                  background: pct > 85 ? 'linear-gradient(90deg, #F26B1F, #EF4444)' : 'linear-gradient(90deg, #10B981, #6BA6F5)',
                }}
              />
            </div>
          )}
          {runningLow && (
            <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-400/25 rounded-full px-2 py-0.5"
                 data-testid="my-allocation-low-warning">
              <TrendingDown size={10} /> Running low — ask admin to top up
            </div>
          )}
        </>
      )}

      {/* Sparkline — 14-day daily burn */}
      <div className="mt-4 h-16">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.daily_consumption || []} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="my-alloc-burn" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" hide />
            <YAxis hide />
            <Tooltip
              contentStyle={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 11 }}
              labelStyle={{ color: 'var(--nexus-text-tertiary)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}
              formatter={(v) => [`${Number(v).toFixed(2)} credits`, 'Spent']}
            />
            <Area type="monotone" dataKey="credits" stroke="#10B981" strokeWidth={1.5} fill="url(#my-alloc-burn)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[9px] uppercase tracking-wider text-nexus-text-tertiary mt-1">
        Last 14 days · daily burn
      </div>
    </motion.div>
  );
};

export default MyAllocationCard;
